import { zodResolver } from '@hookform/resolvers/zod';
import { Label } from '@radix-ui/react-label';
import { updatePassword } from 'aws-amplify/auth';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { changePasswordSchema } from 'schemas/changePasswordSchema';
import { toast } from 'sonner';
import z from 'zod';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { getPasswordCriteria, PasswordCriteriaList } from '../ui/PasswordCriteriaList';

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

export const ChangePasswordForm = ({ onBack }: { onBack: () => void }) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const newPasswordValue = watch('newPassword');
  const confirmPasswordValue = watch('confirmPassword');

  const allCriteriaMet = Object.values(getPasswordCriteria(newPasswordValue)).every(Boolean);
  const passwordsMatch =
    newPasswordValue === confirmPasswordValue && confirmPasswordValue.length > 0;

  const handleSubmitForm = async ({ currentPassword, newPassword }: ChangePasswordFormValues) => {
    setLoading(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      toast.success('Password changed successfully!');
      reset();
      onBack();
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(handleSubmitForm)}>
      <div className="space-y-2">
        <Label htmlFor="currentPassword">Current Password</Label>
        <Input
          id="currentPassword"
          isPassword
          placeholder="Enter current password"
          disabled={loading}
          {...register('currentPassword')}
        />
        {errors.currentPassword && (
          <p className="text-xs text-red-500">{errors.currentPassword.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="up-newPassword">New Password</Label>
        <Input
          id="up-newPassword"
          isPassword
          placeholder="Enter new password"
          disabled={loading}
          {...register('newPassword')}
        />
        {errors.newPassword && <p className="text-xs text-red-500">{errors.newPassword.message}</p>}
      </div>

      <PasswordCriteriaList password={newPasswordValue} />

      <div className="space-y-2">
        <Label htmlFor="up-confirmPassword">Confirm New Password</Label>
        <Input
          id="up-confirmPassword"
          isPassword
          placeholder="Confirm new password"
          disabled={loading}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p className="text-xs text-red-500">{errors.confirmPassword.message}</p>
        )}
      </div>

      <div className="space-y-3 pt-1">
        <Button
          type="submit"
          className="h-12 w-full rounded-2xl text-base font-semibold"
          disabled={!allCriteriaMet || !passwordsMatch || loading}
        >
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
          {loading ? 'Updating...' : 'Change Password'}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="h-12 w-full rounded-2xl text-base"
          disabled={loading}
          onClick={onBack}
        >
          Back
        </Button>
      </div>
    </form>
  );
};
